import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { usePlaceContext } from '../itinerary/PlaceContext';
import '../../styles/components/map_interaction/PlaceInfoBox.css';
import { getReadableTypeName, getTypesToIgnore } from '../../utils/PlaceTypeMapping';
import StarRating from '../common/StarRating';
import PlaceInfoGallery from './PlaceInfoGallery';
import ExtendPlaceInfoBox from './ExtendPlaceInfoBox';
import { v4 as uuidv4 } from 'uuid';
import { debounce } from 'lodash';
import useApi from '../../Api';
import { useAuth } from '../../AuthContext';
import { parseHtmlAttribution } from '../common/parseHtmlAttribution';
import { useNavigate } from 'react-router-dom';
import { validateTextInput } from '../common/TextValidator';
import { toast } from 'react-toastify';
import CreateCustomPlaceWidget from '../itinerary/custom_place/CreateCustomPlaceWidget';
import PropTypes from 'prop-types';

const PlaceInfoBox = ({ onClose, pageName = "default", readOnly = false }) => {
  const { state, savePlace, updatePlaceNotes, updatePlaceDate, updatePlaceTime, setExploreOriginId } = usePlaceContext();
  const { selectedItinerary, savedPlaces, placeInfo, isPlaceInfoBoxVisible, explore_origin_id } = state;
  const [isOpenTimesVisible, setIsOpenTimesVisible] = useState(false);
  const [isGalleryVisible, setIsGalleryVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [notes, setNotes] = useState('');
  const [localDate, setLocalDate] = useState(placeInfo?.date ? new Date(placeInfo.date) : null);
  const [localTime, setLocalTime] = useState(placeInfo?.time ? new Date(placeInfo.time) : null);
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const isExplorePage = pageName === 'ExplorePage';
  const apiClient = useApi();
  const notesUpdateTimeout = useRef(null);
  const dateUpdateTimeout = useRef(null);
  const timeUpdateTimeout = useRef(null);
  const [showEdit, setShowEdit] = useState(false);

  useEffect(() => {
    setErrorMessage('');
    if (placeInfo) {
      setNotes(placeInfo.notes || '');
      setLocalDate(placeInfo.date ? new Date(placeInfo.date) : null);
      setLocalTime(placeInfo.time ? new Date(placeInfo.time) : null);
    }
  }, [placeInfo]);

  const formatNumber = useCallback((number) => {
    if (typeof number !== 'number' || isNaN(number)) {
      return '';
    }
    return number.toLocaleString();
  }, []);

  const getPriceLevelSigns = useCallback((priceLevel) => '$'.repeat(priceLevel || 0), []);

  const typesToIgnore = getTypesToIgnore();
  const filteredTypes = placeInfo.types
    ?.filter(type => !typesToIgnore.includes(type))
    .map(type => getReadableTypeName(type))
    .filter(Boolean);

  const handleClose = useCallback(() => {
    if (pageName === 'ExplorePage') {
      setExploreOriginId(null);
    }
    onClose();
  }, [onClose, pageName, setExploreOriginId]);

  const isSaveDisabled = selectedItinerary?.is_public && !placeInfo?.custom_place_id;

  const handleSavePlace = useCallback(
    debounce(async () => {
      if (!selectedItinerary) {
        setErrorMessage('Please select an itinerary before saving a place.');
        return;
      }
      const itineraryPlaces = savedPlaces[selectedItinerary.id] || [];
      if (itineraryPlaces.length >= 15) {
        toast.error('The maximum number of places in Saved Places is 15.', {
          position: 'top-center',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        return;
      }
      const placeExists = itineraryPlaces.some(savedPlace => savedPlace.name === placeInfo.name);
      if (placeExists) {
        setErrorMessage(`${placeInfo.name} is already in the itinerary.`);
        return;
      }
      const placeWithId = { ...placeInfo, id: uuidv4() };
      try {
        await savePlace(placeWithId);
        setErrorMessage('');
      } catch (error) {
        if (error.response && error.response.data && error.response.data.detail === "Cannot add non-custom places to a public itinerary") {
          toast.error("Please remove non-custom places to make public", {
            position: "top-center",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        } else {
          toast.error("Failed to save place. Please try again.", {
            position: "top-center",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        }
      }
    }, 100),
    [selectedItinerary, savedPlaces, placeInfo, savePlace]
  );

  const handleImageClick = useCallback(() => {
    setIsGalleryVisible(true);
  }, []);

  const handleImageKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        handleImageClick();
      }
    },
    [handleImageClick]
  );

  const closeGallery = useCallback(() => {
    setIsGalleryVisible(false);
  }, []);

  const handleNotesChange = useCallback(
    (newNotes) => {
      if (readOnly) return;
      if (newNotes.trim() === '') {
        setErrorMessage('');
        setNotes('');
        updatePlaceNotes(placeInfo.id, '');
        return;
      }
      const { isValid, errors, sanitizedText } = validateTextInput(newNotes, {
        maxLength: 1000,
        allowSpecialChars: true,
        allowedSpecialChars: '.,!?-_"<>&'
      });
      if (!isValid) {
        setErrorMessage(errors.join(' '));
        return;
      }
      setErrorMessage('');
      setNotes(sanitizedText);
      updatePlaceNotes(placeInfo.id, sanitizedText);
      if (notesUpdateTimeout.current) {
        clearTimeout(notesUpdateTimeout.current);
      }
      notesUpdateTimeout.current = setTimeout(async () => {
        const itineraryId = selectedItinerary.id;
        const columnId = placeInfo.columnId;
        try {
          if (columnId === 'saved-places') {
            const position = savedPlaces[itineraryId].findIndex(place => place.id === placeInfo.id);
            await apiClient.patch(
              `/itineraries/${itineraryId}/saved_places/places/update`,
              {
                notes: sanitizedText,
                google_place_id: placeInfo.google_place_id,
                position: position,
                place_id: placeInfo.place_id
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );
          } else {
            const column = selectedItinerary.columns.find(col => col.id === columnId);
            const position = column.items.findIndex(place => place.id === placeInfo.id);
            await apiClient.patch(
              `/itineraries/${itineraryId}/columns/${columnId}/places/update`,
              {
                notes: sanitizedText,
                google_place_id: placeInfo.google_place_id,
                position: position,
                place_id: placeInfo.place_id
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );
          }
        } catch (error) {
          console.error('Error updating place notes:', error);
        }
      }, 500);
    },
    [readOnly, updatePlaceNotes, placeInfo, selectedItinerary, savedPlaces, apiClient, token]
  );

  const handleDateChange = useCallback(
    (date) => {
      if (readOnly) return;
      setLocalDate(date);
      updatePlaceDate(placeInfo.id, date);
      if (dateUpdateTimeout.current) {
        clearTimeout(dateUpdateTimeout.current);
      }
      dateUpdateTimeout.current = setTimeout(async () => {
        const itineraryId = selectedItinerary.id;
        const columnId = placeInfo.columnId;
        try {
          const formattedDate = date
            ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
            : null;
          if (columnId === 'saved-places') {
            const position = savedPlaces[itineraryId].findIndex(place => place.id === placeInfo.id);
            await apiClient.patch(
              `/itineraries/${itineraryId}/saved_places/places/update`,
              {
                date: formattedDate,
                google_place_id: placeInfo.google_place_id,
                position: position,
                place_id: placeInfo.place_id
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );
          } else {
            const column = selectedItinerary.columns.find(col => col.id === columnId);
            const position = column.items.findIndex(place => place.id === placeInfo.id);
            await apiClient.patch(
              `/itineraries/${itineraryId}/columns/${columnId}/places/update`,
              {
                date: formattedDate,
                google_place_id: placeInfo.google_place_id,
                position: position,
                place_id: placeInfo.place_id
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );
          }
        } catch (error) {
          console.error('Error updating place date:', error);
        }
      }, 500);
    },
    [readOnly, updatePlaceDate, placeInfo, selectedItinerary, savedPlaces, apiClient, token]
  );

  const handleTimeChange = useCallback(
    (time) => {
      if (readOnly) return;
      setLocalTime(time);
      updatePlaceTime(placeInfo.id, time);
      if (timeUpdateTimeout.current) {
        clearTimeout(timeUpdateTimeout.current);
      }
      timeUpdateTimeout.current = setTimeout(async () => {
        const itineraryId = selectedItinerary.id;
        const columnId = placeInfo.columnId;
        try {
          const formattedTime = time
            ? `${String(time.getHours()).padStart(2, '0')}:${String(time.getMinutes()).padStart(2, '0')}:${String(time.getSeconds()).padStart(2, '0')}`
            : null;
          if (columnId === 'saved-places') {
            const position = savedPlaces[itineraryId].findIndex(place => place.id === placeInfo.id);
            await apiClient.patch(
              `/itineraries/${itineraryId}/saved_places/places/update`,
              {
                time: formattedTime,
                google_place_id: placeInfo.google_place_id,
                position: position,
                place_id: placeInfo.place_id
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );
          } else {
            const column = selectedItinerary.columns.find(col => col.id === columnId);
            const position = column.items.findIndex(place => place.id === placeInfo.id);
            await apiClient.patch(
              `/itineraries/${itineraryId}/columns/${columnId}/places/update`,
              {
                time: formattedTime,
                google_place_id: placeInfo.google_place_id,
                position: position,
                place_id: placeInfo.place_id
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );
          }
        } catch (error) {
          console.error('Error updating place time:', error);
        }
      }, 500);
    },
    [readOnly, updatePlaceTime, placeInfo, selectedItinerary, savedPlaces, apiClient, token]
  );

  const displayLocation = useCallback(() => {
    if (placeInfo.location) {
      return placeInfo.location;
    }
    if (placeInfo.city && placeInfo.country) {
      return `${placeInfo.city}, ${placeInfo.country}`;
    } else if (placeInfo.city) {
      return placeInfo.city;
    } else if (placeInfo.country) {
      return placeInfo.country;
    } else {
      return 'Custom Place';
    }
  }, [placeInfo]);

  const handleNavigate = useCallback(() => {
    if (explore_origin_id) {
      navigate(`/destination/${explore_origin_id}`);
    } else {
      console.error('Origin ID is not available');
    }
  }, [explore_origin_id, navigate]);

  if (!isPlaceInfoBoxVisible || !placeInfo) return null;
  if (showEdit)
    return (
      <CreateCustomPlaceWidget
        onClose={() => setShowEdit(false)}
        latitude={placeInfo.latitude}
        longitude={placeInfo.longitude}
        existingPlace={placeInfo}
        onUpdate={(updatedPlace) => {}}
      />
    );

  const galleryImagesFinal = placeInfo.photos && placeInfo.photos.length > 0
    ? placeInfo.photos
    : (placeInfo.photoUrl ? [{
        photo_reference: placeInfo.photoUrl,
        html_attributions: placeInfo.html_attributions || []
      }] : []);
  const mainImageSrcFinal = placeInfo.photoUrl
    ? placeInfo.photoUrl
    : (galleryImagesFinal.length > 0 && galleryImagesFinal[0].url ? galleryImagesFinal[0].url : null);
  const hasImageFinal = mainImageSrcFinal && mainImageSrcFinal.trim() !== "";

  return (
    <div className={`placeinfobox-container ${!hasImageFinal ? 'no-image' : ''}`}>
      <div className="placeinfobox-header-buttons">
        {!readOnly && placeInfo.custom_place_id && placeInfo.creator_id === user.uid && (
          <button className="placeinfobox-edit-button" onClick={() => setShowEdit(true)} aria-label={`Edit ${placeInfo.name}`}>
            Edit
          </button>
        )}
        <button className="placeinfobox-close-button" onClick={handleClose} aria-label="Close Place Info Box">
          ×
        </button>
      </div>
      {hasImageFinal && (
        <>
          <img
            src={mainImageSrcFinal}
            alt={placeInfo.name}
            className="placeinfobox-image"
            loading="lazy"
            onClick={handleImageClick}
            onKeyDown={handleImageKeyDown}
            role="button"
            tabIndex="0"
            aria-label={`Open gallery for ${placeInfo.name}`}
          />
          {placeInfo.photoUrl && placeInfo.html_attributions && placeInfo.html_attributions.length > 0 && (
            <div className="placeinfobox-photo-attribution">
              {placeInfo.html_attributions.map((attribution, index) => (
                <span key={index}>{parseHtmlAttribution(attribution)}</span>
              ))}
            </div>
          )}
        </>
      )}
      {placeInfo.icon && (
        <img
          src={placeInfo.icon}
          alt={`${placeInfo.name} icon`}
          className="placeinfobox-icon"
          loading="lazy"
        />
      )}
      <div className="placeinfobox-content">
        <h3 className="placeinfobox-title">{placeInfo.name}</h3>
        {placeInfo.rating > 0 && (
          <div className="placeinfobox-rating">
            <span className="placeinfobox-rating-number">{placeInfo.rating}</span>
            <StarRating rating={parseFloat(placeInfo.rating) || 0} />
            {placeInfo.user_ratings_total && (
              <span className="placeinfobox-rating-total">
                ({formatNumber(placeInfo.user_ratings_total)} reviews)
              </span>
            )}
          </div>
        )}
        {filteredTypes?.length > 0 && (
          <span className="placeinfobox-types">{filteredTypes.join(', ')}</span>
        )}
        <div className="placeinfobox-price-and-types">
          {placeInfo.price_level !== undefined && placeInfo.price_level > 0 && (
            <div className="placeinfobox-price-level-box">
              <span className="placeinfobox-price-level">{getPriceLevelSigns(placeInfo.price_level)}</span>
            </div>
          )}
        </div>
        {placeInfo.about && <p className="placeinfobox-about">{placeInfo.about}</p>}
        {placeInfo.website && (
          <p>
            <a
              href={placeInfo.website}
              target="_blank"
              rel="noopener noreferrer"
              className="placeinfobox-website-button"
              aria-label={`Visit website for ${placeInfo.name}`}
            >
              Visit Website
            </a>
          </p>
        )}
        {placeInfo.business_status && placeInfo.business_status !== 'OPERATIONAL' && (
          <p className="placeinfobox-business-status">{placeInfo.business_status}</p>
        )}
        {placeInfo.opening_hours?.length > 0 && (
          <div className="placeinfobox-opening-hours">
            <h4
              onClick={() => setIsOpenTimesVisible(!isOpenTimesVisible)}
              className="placeinfobox-opening-hours-toggle"
              tabIndex="0"
              role="button"
              aria-label="Toggle opening hours"
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setIsOpenTimesVisible(!isOpenTimesVisible); }}
            >
              Opening Hours {isOpenTimesVisible ? '▲' : '▼'}
            </h4>
            {isOpenTimesVisible && (
              <ul>
                {placeInfo.opening_hours.map((hours, index) => (
                  <li key={index}>{hours}</li>
                ))}
              </ul>
            )}
          </div>
        )}
        {placeInfo.formatted_address && placeInfo.formatted_address.trim() !== '' ? (
          <p className="placeinfobox-address">{placeInfo.formatted_address}</p>
        ) : (
          (placeInfo.location || placeInfo.city || placeInfo.country) && (
            <p className="placeinfobox-city-country">{displayLocation()}</p>
          )
        )}
        {!readOnly && !placeInfo.fromWidget && (
          <>
            {errorMessage && <div className="placeinfobox-error-message" aria-live="polite">{errorMessage}</div>}
            <div className="placeinfobox-save-container">
              {isExplorePage && explore_origin_id ? (
                <button className="placeinfobox-navigate-button" onClick={handleNavigate} aria-label="Explore destination">
                  Explore
                </button>
              ) : (
                !isExplorePage && (
                  isSaveDisabled ?
                    <span title="Cannot add non-custom places to a public itinerary">
                      <button className="placeinfobox-save-button" disabled aria-label="Save place">
                        Save Place
                      </button>
                    </span>
                  :
                    <button className="placeinfobox-save-button" onClick={handleSavePlace} aria-label="Save place">
                      Save Place
                    </button>
                )
              )}
            </div>
          </>
        )}
        <ExtendPlaceInfoBox
          showExtension={placeInfo.showExtension}
          notes={notes}
          onNotesChange={handleNotesChange}
          date={localDate}
          onDateChange={handleDateChange}
          time={localTime}
          onTimeChange={handleTimeChange}
          readOnly={readOnly}
        />
      </div>
      {isGalleryVisible && (
        <PlaceInfoGallery images={galleryImagesFinal} initialIndex={0} onClose={closeGallery} />
      )}
    </div>
  );
};

PlaceInfoBox.propTypes = {
  onClose: PropTypes.func.isRequired,
  pageName: PropTypes.string,
  readOnly: PropTypes.bool,
};

export default memo(PlaceInfoBox);
